import { useCallback, useRef } from 'react';
import nipplejs from 'nipplejs';
import * as THREE from 'three';

export const useMobileControls = ({ container, camera, controls, isMobile }) => {
  const joystickRef = useRef();
  const movementDirectionRef = useRef(new THREE.Vector3());
  const animationIdRef = useRef();

  const setupMobileControls = useCallback(() => {
    if (!container || !isMobile || joystickRef.current) return;

    const joystick = nipplejs.create({
      zone: container,
      mode: 'static',
      position: { left: '50%', top: '50%' },
      color: 'rgba(0, 238, 255, 0.5)',
      size: 120
    });

    joystick.on('move', (evt, data) => {
      const angle = data.angle.radian;
      const force = Math.min(data.force, 1);
      
      movementDirectionRef.current.x = -Math.cos(angle) * force;
      movementDirectionRef.current.z = -Math.sin(angle) * force;
    });

    joystick.on('end', () => {
      movementDirectionRef.current.set(0, 0, 0);
    });

    joystickRef.current = joystick;

    // Movement update loop
    const updateMovement = () => {
      if (!camera || !controls || movementDirectionRef.current.length() === 0) {
        animationIdRef.current = requestAnimationFrame(updateMovement);
        return;
      }

      // Convert movement direction from local to global coordinates
      const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(camera.quaternion);
      forward.y = 0;
      forward.normalize();

      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
      right.y = 0;
      right.normalize();

      const moveVector = new THREE.Vector3();
      moveVector.addScaledVector(forward, -movementDirectionRef.current.z * 0.1);
      moveVector.addScaledVector(right, movementDirectionRef.current.x * 0.1);

      controls.target.add(moveVector);
      camera.position.add(moveVector);

      animationIdRef.current = requestAnimationFrame(updateMovement);
    };

    updateMovement();

    return () => {
      if (joystickRef.current) {
        joystickRef.current.destroy();
        joystickRef.current = null;
      }
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [container, camera, controls, isMobile]);

  return { setupMobileControls };
};